import { useEffect, useRef, useState } from 'react';

export interface AiImage {
  filename: string;
  contentType: string;
  base64: string;
  note?: string;
  previewUrl: string;
}

export async function filesToAiImages(files: FileList | File[]): Promise<AiImage[]> {
  return Promise.all(
    Array.from(files).map(
      (f) =>
        new Promise<AiImage>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = String(reader.result);
            resolve({ filename: f.name, contentType: f.type || 'image/png', base64: dataUrl.split(',')[1] ?? '', previewUrl: dataUrl });
          };
          reader.readAsDataURL(f);
        }),
    ),
  );
}

/** Attach images to an AI request, with a note per image telling the model where it goes. */
export function AiImagePicker({ images, onChange, disabled }: { images: AiImage[]; onChange: (images: AiImage[]) => void; disabled?: boolean }) {
  const [dragging, setDragging] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  // Keep the latest callback/images without re-binding the window listener.
  const latest = useRef({ images, onChange, disabled });
  latest.current = { images, onChange, disabled };

  const addFiles = async (files: File[]) => {
    const imgs = files.filter((f) => f.type.startsWith('image/'));
    if (!imgs.length || latest.current.disabled) return;
    const named = imgs.map((f, i) => (f.name && f.name !== 'image.png' ? f : new File([f], `pasted-${Date.now()}-${i + 1}.${(f.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`, { type: f.type })));
    const next = [...latest.current.images, ...(await filesToAiImages(named))].slice(0, 10);
    latest.current.onChange(next);
    setFlash(`Added ${named.length} image${named.length > 1 ? 's' : ''} from ${named[0].name.startsWith('pasted-') ? 'clipboard' : 'files'}`);
    setTimeout(() => setFlash(null), 2500);
  };

  // Paste an image anywhere on the screen → it's attached.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const files = Array.from(e.clipboardData?.items ?? [])
        .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
        .map((it) => it.getAsFile())
        .filter((f): f is File => !!f);
      if (!files.length) return;
      e.preventDefault();
      void addFiles(files);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  return (
    <div
      className="space-y-2"
      onDragOver={(e) => {
        if (Array.from(e.dataTransfer.types).includes('Files')) {
          e.preventDefault();
          setDragging(true);
        }
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        void addFiles(Array.from(e.dataTransfer.files));
      }}
    >
      <label className={`flex cursor-pointer items-center gap-2 rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground hover:bg-muted ${dragging ? 'border-[var(--ff-primary)] bg-[var(--ff-primary-subtle)]' : ''}`}>
        <span>＋ Attach images the email should use — click, drag files here, or just <b>paste</b> an image (⌘V) anywhere on this page</span>
        <input
          type="file"
          accept="image/*"
          multiple
          disabled={disabled}
          className="hidden"
          onChange={async (e) => {
            if (!e.target.files?.length) return;
            onChange([...images, ...(await filesToAiImages(e.target.files))].slice(0, 10));
            e.target.value = '';
          }}
        />
      </label>
      {flash && <p className="text-xs text-emerald-700">{flash}</p>}
      {images.length > 0 && (
        <ul className="space-y-2">
          {images.map((img, i) => (
            <li key={i} className="flex items-center gap-2 rounded-md border p-2">
              <img src={img.previewUrl} alt={img.filename} className="size-12 rounded object-cover" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs">{img.filename}</div>
                <input
                  value={img.note ?? ''}
                  placeholder="where / what is it? e.g. hero image at the top"
                  className="mt-1 h-8 w-full rounded border px-2 text-xs"
                  onChange={(e) => onChange(images.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))}
                />
              </div>
              <button type="button" className="text-xs text-muted-foreground hover:text-red-600" onClick={() => onChange(images.filter((_, j) => j !== i))}>
                remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
